import React, { useState } from "react";
import { Grid, Button, CircularProgress, Stack, TextField } from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import EmailIcon from "@mui/icons-material/Email";
import { api } from "../../helpers/apiRequest";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import colors from "../../theme/Colors";
import { exportToPDF, exportToExcel, getPDFBlob } from "./ReportUtils";
import EmailReportDialog from "../EmailReportDialog";

const OrderPaymentReport = ({ customers }) => {
  const [loadingReport, setLoadingReport] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedEmailCustomer, setSelectedEmailCustomer] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [filters, setFilters] = useState({
    orderId: "",
  });

  const generateReport = async (exportType = "html") => {
    if (!filters.orderId) {
      toast.error("Please enter an Order ID");
      return;
    }

    setLoadingReport(true);
    try {
      const response = await api.get(`/orders/payments/${filters.orderId}`);

      if (response.data && response.data.payments.length > 0) {
        const payments = response.data.payments;

        // Excel Export
        if (exportType === "excel") {
          const excelData = payments.map((p) => ({
            "Order ID": filters.orderId,
            Amount: p.amount,
            Date: dayjs(p.date).format("YYYY-MM-DD HH:mm"),
            Type: p.type,
          }));

          exportToExcel(
            excelData,
            `Order_Payment_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}`
          );
          toast.success("Excel report generated successfully");
          return;
        }

        // HTML Content
        let htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Order Payment Report</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 15px; padding: 30px; }
              h1 { color: #1abc9c; text-align: center; }
              table { border-collapse: collapse; width: 95%; margin: 15px auto; }
              th { background-color: #1abc9c; color: white; text-align: left; padding: 8px; }
              td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              tr:nth-child(even) { background-color: #f2f2f2; }
              .report-header { margin-bottom: 20px; text-align: center; }
              .report-filters { margin: 15px; font-size: 14px; }
            </style>
          </head>
          <body>
            <div class="report-header">
              <h1>Order Payment Report</h1>
              <div class="report-filters">
                <p><strong>Order ID:</strong> ${filters.orderId}</p>
                <p>Generated on: ${dayjs().format("YYYY-MM-DD HH:mm")}</p>
              </div>
            </div>
            
            <table>
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Amount</th>
                  <th>Date</th>
                  <th>Type</th>
                </tr>
              </thead>
              <tbody>
        `;

        payments.forEach((p) => {
          htmlContent += `
            <tr>
              <td>${filters.orderId}</td>
              <td>${p.amount}</td>
              <td>${dayjs(p.date).format("YYYY-MM-DD HH:mm")}</td>
              <td>${p.type}</td>
            </tr>
          `;
        });

        htmlContent += `
              </tbody>
            </table>
          </body>
          </html>
        `;

        // PDF Export
        if (exportType === "pdf") {
          exportToPDF(
            htmlContent,
            `Order_Payment_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}`
          );
          toast.success("PDF report generated successfully");
          return;
        }

        // Email Export
        if (exportType === "email") {
          setEmailDialogOpen(true);
          setLoadingReport(false);
          return;
        }

        // Open in New Tab (HTML View)
        const reportWindow = window.open("", "_blank");
        if (!reportWindow) {
          toast.error("Please allow popups for this site to view reports");
          return;
        }
        reportWindow.document.write(htmlContent);
        reportWindow.document.close();

        toast.success("Report generated successfully");
      } else {
        toast.error("No payments found for this Order ID");
      }
    } catch (error) {
      console.error("Error generating order payment report:", error);
      toast.error("Failed to generate order payment report");
    } finally {
      setLoadingReport(false);
    }
  };

  const sendReportByEmail = async () => {
    if (!selectedEmailCustomer) {
      toast.error("Please select a customer");
      return;
    }
    if (!filters.orderId) {
      toast.error("Please enter an Order ID");
      return;
    }

    setSendingEmail(true);
    try {
      const response = await api.get(`/orders/payments/${filters.orderId}`);
      if (response.data && response.data.payments.length > 0) {
        const payments = response.data.payments;

        let htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Order Payment Report</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 15px; padding: 30px; }
              h1 { color: #1abc9c; text-align: center; }
              table { border-collapse: collapse; width: 95%; margin: 15px auto; }
              th { background-color: #1abc9c; color: white; text-align: left; padding: 8px; }
              td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              tr:nth-child(even) { background-color: #f2f2f2; }
              .report-header { margin-bottom: 20px; text-align: center; }
              .report-filters { margin: 15px; font-size: 14px; }
            </style>
          </head>
          <body>
            <div class="report-header">
              <h1>Order Payment Report</h1>
              <div class="report-filters">
                <p><strong>Order ID:</strong> ${filters.orderId}</p>
              </div>
              <p>Generated on: ${dayjs().format("YYYY-MM-DD HH:mm")}</p>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Amount</th>
                  <th>Date</th>
                  <th>Type</th>
                </tr>
              </thead>
              <tbody>
        `;

        payments.forEach((p) => {
          htmlContent += `
            <tr>
              <td>${filters.orderId}</td>
              <td>${p.amount}</td>
              <td>${dayjs(p.date).format("YYYY-MM-DD HH:mm")}</td>
              <td>${p.type}</td>
            </tr>
          `;
        });

        htmlContent += `
              </tbody>
            </table>
          </body>
          </html>
        `;

        const pdfBlob = await getPDFBlob(htmlContent);

        const formData = new FormData();
        formData.append(
          "image",
          pdfBlob,
          `Order_Payment_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}.pdf`
        );
        formData.append("userId", selectedEmailCustomer);
        formData.append("type", "Report");

        const emailResponse = await api.post("inventory/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (emailResponse.status === 200) {
          toast.success("Report sent by email successfully");
          setEmailDialogOpen(false);
          setSelectedEmailCustomer("");
        } else {
          toast.error("Failed to send report by email");
        }
      } else {
        toast.error("No payments found for this Order ID");
      }
    } catch (error) {
      console.error("Error sending report by email:", error);
      toast.error("Failed to send report by email");
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <Grid container spacing={3} alignItems="center">
      <Grid item xs={12} sm={10}>
        <TextField
          fullWidth
          label="Order ID"
          value={filters.orderId}
          onChange={(e) => setFilters({ ...filters, orderId: e.target.value })}
          placeholder="Enter Order ID"
        />
      </Grid>

      <Grid item xs={12} sm={2}>
        <Stack spacing={1}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => generateReport("html")}
            disabled={loadingReport || sendingEmail}
            startIcon={<DescriptionIcon />}
            sx={{
              height: "38px",
              backgroundColor: colors.primary,
              "&:hover": { backgroundColor: colors.primary, opacity: 0.9 },
            }}
          >
            {loadingReport ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "View"
            )}
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateReport("pdf")}
            disabled={loadingReport || sendingEmail}
            startIcon={<PictureAsPdfIcon />}
            sx={{
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              "&:hover": { borderColor: colors.primary, opacity: 0.9 },
            }}
          >
            PDF
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateReport("excel")}
            disabled={loadingReport || sendingEmail}
            startIcon={<FileDownloadIcon />}
            sx={{
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              "&:hover": { borderColor: colors.primary, opacity: 0.9 },
            }}
          >
            Excel
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateReport("email")}
            disabled={loadingReport || sendingEmail}
            startIcon={<EmailIcon />}
            sx={{
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              "&:hover": { borderColor: colors.primary, opacity: 0.9 },
            }}
          >
            Email
          </Button>
        </Stack>
      </Grid>

      <EmailReportDialog
        open={emailDialogOpen}
        handleClose={() => {
          setEmailDialogOpen(false);
          setSelectedEmailCustomer("");
        }}
        selectedCustomer={selectedEmailCustomer}
        setSelectedCustomer={setSelectedEmailCustomer}
        customers={customers}
        isLoading={sendingEmail}
        handleSendEmail={sendReportByEmail}
      />
    </Grid>
  );
};

export default OrderPaymentReport;
